import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AddressBookService from '../services/AddressBookService';
import { notifyAddressBookUpdated } from './AddressDisplay';

/**
 * AddressAddModal Component
 *
 * Handles URL-based address addition from shareable links
 * URL format: /address_add?name=PlayerName&address=0x123...
 */
const AddressAddModal = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [addressData, setAddressData] = useState(null);
  const [editableName, setEditableName] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Parse URL parameters
    const address = searchParams.get('address');
    const name = searchParams.get('name');

    if (!address) {
      setError('No address provided in URL');
      return;
    }

    // Validate address format
    if (!AddressBookService.isValidAddress(address)) {
      setError('Invalid address format');
      return;
    }

    // Check if address already exists in address book
    const existingEntries = AddressBookService.getAllEntries();
    const normalizedAddress = AddressBookService.normalizeAddress(address);
    const existingEntry = existingEntries.find(
      entry => AddressBookService.normalizeAddress(entry.address) === normalizedAddress
    );

    const displayName = name || `Wallet ${address.slice(0, 6)}...${address.slice(-4)}`;

    setAddressData({
      address,
      originalName: displayName,
      existingEntry
    });

    setEditableName(displayName);
  }, [searchParams]);

  const handleAdd = async () => {
    if (!addressData || !editableName.trim()) {
      setError('Please enter a name for this address');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');

      const success = AddressBookService.setNickname(addressData.address, editableName.trim());

      if (success) {
        // Notify other components that address book was updated
        notifyAddressBookUpdated();

        // Redirect to main page with success message
        navigate('/', {
          state: {
            message: `Added "${editableName.trim()}" to your address book!`,
            type: 'success'
          }
        });
      } else {
        setError('Failed to add address to address book');
      }
    } catch (err) {
      console.error('Error adding address:', err);
      setError('An error occurred while adding the address');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdate = async () => {
    if (!addressData || !editableName.trim()) {
      setError('Please enter a name for this address');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');

      const success = AddressBookService.setNickname(addressData.address, editableName.trim());

      if (success) {
        // Notify other components that address book was updated
        notifyAddressBookUpdated();

        // Redirect to main page with success message
        navigate('/', {
          state: {
            message: `Updated "${editableName.trim()}" in your address book!`,
            type: 'success'
          }
        });
      } else {
        setError('Failed to update address in address book');
      }
    } catch (err) {
      console.error('Error updating address:', err);
      setError('An error occurred while updating the address');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    navigate('/', {
      state: {
        message: 'Address addition cancelled',
        type: 'info'
      }
    });
  };

  // Show error page if no valid address data
  if (error && !addressData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Address Link</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              Go to GameMaker
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-blue-500 text-6xl mb-4">📱</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {addressData?.existingEntry ? 'Update Address' : 'Add Address'}
          </h1>
          <p className="text-gray-600">
            {addressData?.existingEntry
              ? 'This address is already in your address book. You can update the name.'
              : 'Add this address to your address book for easy access.'
            }
          </p>
        </div>

        {/* Address Information */}
        {addressData && (
          <div className="mb-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wallet Address
              </label>
              <div className="font-mono text-xs text-gray-900 break-all bg-white p-2 rounded border">
                {addressData.address}
              </div>

              {addressData.existingEntry && (
                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-xs text-blue-800">
                    <strong>Currently saved as:</strong> {addressData.existingEntry.nickname}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Editable Name Field */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Display Name
          </label>
          <input
            type="text"
            value={editableName}
            onChange={(e) => setEditableName(e.target.value)}
            placeholder="Enter a friendly name..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isProcessing}
          />
          <p className="text-xs text-gray-500 mt-1">
            Choose a name that helps you identify this wallet
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={addressData?.existingEntry ? handleUpdate : handleAdd}
            disabled={isProcessing || !editableName.trim()}
            className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
              isProcessing || !editableName.trim()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isProcessing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {addressData?.existingEntry ? 'Updating...' : 'Adding...'}
              </div>
            ) : (
              <>
                {addressData?.existingEntry ? 'Update Address' : 'Add to Address Book'}
              </>
            )}
          </button>

          <button
            onClick={handleCancel}
            disabled={isProcessing}
            className="w-full py-2 px-4 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            This will save the address to your local browser storage.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AddressAddModal;